import React, { useState, useEffect } from "react";
import { Outlet } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "../../utils/cn";
import { useSelector } from "react-redux";
import { toggle } from "../../store/slices/sidebarSlice";
import Header from "./Header";
import Sidebar from "./Sidebar";
import Footer from "./Footer";
import { useLocation } from "react-router-dom";
import { useAppDispatch } from "../../store/hooks";
import { useAuth } from "../../hooks/useAuth";

export interface AppLayoutProps {
  className?: string;
  showSidebar?: boolean;
  onSidebarClose?: () => void;
}

const AppLayout: React.FC<AppLayoutProps> = ({
  className,
  showSidebar = true,
  onSidebarClose,
}) => {
  const [isMobile, setIsMobile] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const dispatch = useAppDispatch();
  const collapsed = useSelector((state: any) => state.sidebar.collapsed);
  const location = useLocation();

  const { user } = useAuth();

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  useEffect(() => {
    if (isMobile) {
      setIsSidebarOpen(false);
    }
  }, [isMobile]);

  const handleSidebarToggle = () => {
    if (isMobile) {
      const newState = !isSidebarOpen;
      setIsSidebarOpen(newState);
    } else {
      dispatch(toggle());
    }
  };

  const handleSidebarClose = () => {
    setIsSidebarOpen(false);
    onSidebarClose?.();
  };

  return (
    <>
      <div
        className={cn(
          "h-screen bg-white dark:bg-gray-900 flex overflow-hidden",
          className,
        )}
      >
        {/* Sidebar */}
        <AnimatePresence>
          {showSidebar && (
            <motion.div
              initial={isMobile ? { x: "-100%" } : false}
              animate={isMobile ? { x: isSidebarOpen ? 0 : "-100%" } : {}}
              exit={isMobile ? { x: "-100%" } : {}}
              transition={{ duration: 0.3, ease: "easeInOut" }}
              className={cn(
                "fixed lg:relative z-50 h-screen flex-shrink-0",
                isMobile ? "w-80" : collapsed ? "w-16" : "w-64",
              )}
            >
              <Sidebar
                collapsed={!isMobile && collapsed}
                onToggle={handleSidebarToggle}
                onClose={handleSidebarClose}
                isMobile={isMobile}
              />
            </motion.div>
          )}
        </AnimatePresence>

        {/* Mobile Overlay */}
        <AnimatePresence>
          {isMobile && isSidebarOpen && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
              onClick={handleSidebarClose}
            />
          )}
        </AnimatePresence>

        {/* Main Content Area */}
        <motion.div
          layout
          transition={{ type: "spring", stiffness: 300, damping: 30 }}
          className={cn(
            "flex-1 flex flex-col",
            isMobile ? "w-80" : collapsed ? "w-16" : "w-64",
          )}
        >
          {/* Header - Full width */}
          <Header
            onSidebarToggle={handleSidebarToggle}
            showSidebar={showSidebar}
          />

          {/* Main Content */}
          <motion.main
            key={location.pathname}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.25, ease: "easeInOut" }}
            className="flex-1 min-h-0 bg-gray-50 dark:bg-gray-800"
          >
            <div className="h-full overflow-y-auto p-4 lg:p-6 content-area-transition">
              <Outlet />
            </div>
          </motion.main>

          {/* Footer */}
          <Footer />
        </motion.div>
      </div>
    </>
  );
};

export default AppLayout;
